import React, { useState } from "react";
import { View, StyleSheet } from "react-native";
import {
  Checkbox,
  TextInput,
  IconButton,
  Text,
  Button,
} from "react-native-paper";
import { useTranslation } from "react-i18next";
import { Field } from "../../config/companies/config";
import { ChecklistItemValue, ChecklistValue } from "./checklistTypes";

interface ChecklistFieldProps {
  field: Field;
  value: ChecklistValue[];
  onChange: (fieldName: string, newValue: any) => void;
  errors?: { [key: string]: string };
}

interface ChecklistSectionProps {
  checklist: ChecklistValue;
  onToggleItem: (checklistId: string, itemId: string) => void;
  onRemoveItem: (checklistId: string, itemId: string) => void;
  onAddItem: (checklistId: string, itemText: string) => void;
  onRemoveChecklist: (checklistId: string) => void;
  t: (key: string) => string;
}

const ChecklistSection = ({
  checklist,
  onToggleItem,
  onRemoveItem,
  onAddItem,
  onRemoveChecklist,
  t,
}: ChecklistSectionProps) => {
  const [newItemText, setNewItemText] = useState("");

  const handleAddItem = () => {
    if (newItemText.trim()) {
      onAddItem(checklist.id, newItemText);
      setNewItemText("");
    }
  };

  return (
    <View style={styles.checklistSection}>
      <View style={styles.checklistHeader}>
        <Text style={styles.checklistTitle}>{checklist.title}</Text>
        <IconButton
          icon="delete"
          size={20}
          onPress={() => onRemoveChecklist(checklist.id)}
        />
      </View>

      {checklist.items.map((item) => (
        <View key={item.id} style={styles.itemContainer}>
          <Checkbox.Item
            label={item.text}
            status={item.checked ? "checked" : "unchecked"}
            onPress={() => onToggleItem(checklist.id, item.id)}
            style={styles.checkboxItem}
            labelStyle={item.checked ? styles.checkedText : undefined}
            position="leading"
          />
          <IconButton
            icon="delete"
            size={20}
            onPress={() => onRemoveItem(checklist.id, item.id)}
          />
        </View>
      ))}

      <View style={styles.inputContainer}>
        <TextInput
          mode="outlined"
          label={t("Add item")}
          value={newItemText}
          onChangeText={setNewItemText}
          style={styles.input}
          right={
            <TextInput.Icon
              icon="plus"
              onPress={handleAddItem}
              disabled={!newItemText.trim()}
            />
          }
          onSubmitEditing={handleAddItem}
        />
      </View>
    </View>
  );
};

export const ChecklistField = ({
  field,
  value,
  onChange,
  errors = {},
}: ChecklistFieldProps) => {
  const { t } = useTranslation();
  const [newChecklistTitle, setNewChecklistTitle] = useState("");
  const [showAddChecklist, setShowAddChecklist] = useState(false);

  // Ensure the value is always an array
  const checklists = Array.isArray(value) ? value : [];

  const addChecklist = () => {
    if (newChecklistTitle.trim()) {
      const newChecklist: ChecklistValue = {
        id: Date.now().toString(),
        title: newChecklistTitle.trim(),
        items: [],
      };
      onChange(field.name, [...checklists, newChecklist]);
      setNewChecklistTitle("");
      setShowAddChecklist(false);
    }
  };

  const removeChecklist = (checklistId: string) => {
    const updatedChecklists = checklists.filter((cl) => cl.id !== checklistId);
    onChange(field.name, updatedChecklists);
  };

  const addItem = (checklistId: string, itemText: string) => {
    if (itemText.trim()) {
      const newItem: ChecklistItemValue = {
        id: Date.now().toString(),
        text: itemText.trim(),
        checked: false,
      };
      const updatedChecklists = checklists.map((checklist) =>
        checklist.id === checklistId
          ? { ...checklist, items: [...checklist.items, newItem] }
          : checklist,
      );
      onChange(field.name, updatedChecklists);
    }
  };

  const toggleItem = (checklistId: string, itemId: string) => {
    const updatedChecklists = checklists.map((checklist) =>
      checklist.id === checklistId
        ? {
            ...checklist,
            items: checklist.items.map((item) =>
              item.id === itemId ? { ...item, checked: !item.checked } : item,
            ),
          }
        : checklist,
    );
    onChange(field.name, updatedChecklists);
  };

  const removeItem = (checklistId: string, itemId: string) => {
    const updatedChecklists = checklists.map((checklist) =>
      checklist.id === checklistId
        ? {
            ...checklist,
            items: checklist.items.filter((item) => item.id !== itemId),
          }
        : checklist,
    );
    onChange(field.name, updatedChecklists);
  };

  const label = t(field.label) + (field.required ? "*" : "");

  return (
    <View style={styles.container}>
      {!field.hideLabel && <Text style={styles.label}>{label}</Text>}

      {checklists.map((checklist) => (
        <ChecklistSection
          key={checklist.id}
          checklist={checklist}
          onToggleItem={toggleItem}
          onRemoveItem={removeItem}
          onAddItem={addItem}
          onRemoveChecklist={removeChecklist}
          t={t}
        />
      ))}

      {showAddChecklist ? (
        <View style={styles.addChecklistContainer}>
          <TextInput
            mode="outlined"
            label={t("Checklist title")}
            value={newChecklistTitle}
            onChangeText={setNewChecklistTitle}
            style={styles.input}
            autoFocus
            right={
              <TextInput.Icon
                icon="check"
                onPress={addChecklist}
                disabled={!newChecklistTitle.trim()}
              />
            }
            onSubmitEditing={addChecklist}
          />
          <IconButton
            icon="close"
            size={20}
            onPress={() => {
              setShowAddChecklist(false);
              setNewChecklistTitle("");
            }}
          />
        </View>
      ) : (
        <Button
          mode="outlined"
          icon="plus"
          onPress={() => setShowAddChecklist(true)}
          style={styles.addButton}
        >
          {t("Add checklist")}
        </Button>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
  },
  checklistSection: {
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 8,
    padding: 12,
  },
  checklistHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  checklistTitle: {
    fontSize: 18,
    fontWeight: "bold",
  },
  itemContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  checkboxItem: {
    flex: 1,
    paddingLeft: 0,
  },
  checkedText: {
    textDecorationLine: "line-through",
    opacity: 0.6,
  },
  inputContainer: {
    marginTop: 8,
  },
  input: {
    flex: 1,
  },
  addButton: {
    marginTop: 8,
  },
  addChecklistContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
});
